import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { auditService } from '../../services/auditService';
import { useAuth } from '../../context/AuthContext';
import './ClientAudits.css';
import { ScheduleNumber } from '../../components/ScheduleStatus';
import { PageLoader } from '../../components/Loader';
import { ScheduleKpiBoard } from '../../components/ScheduleKpis';

const GROUPS = [
  { key: 'live', label: 'Going on now' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'ended', label: 'Finished' },
];

function outletText(audit) {
  if (!audit.outlet_name) return '';
  return `${audit.outlet_name}${audit.outlet_code ? ` (${audit.outlet_code})` : ''}`;
}

/** Status from the audit times, in case the server did not send one. */
function statusOf(audit) {
  if (audit.status) return audit.status;
  const now = Date.now();
  if (now < new Date(audit.start_at).getTime()) return 'upcoming';
  if (now > new Date(audit.end_at).getTime()) return 'ended';
  return 'live';
}

function scopeText(user, audits) {
  const orgName = audits.find((audit) => audit.organization_name)?.organization_name;
  if (user?.client_access_level === 'organization') {
    return orgName ? `All outlets of ${orgName}` : 'All outlets of your organization';
  }
  if (user?.client_access_level === 'outlet') {
    return 'Your outlets';
  }
  return '';
}

/**
 * Home page for client users: the audits they can view. Head office sees
 * every outlet's audits; store / area managers see only their outlets.
 */
function ClientAudits() {
  const { user, logout } = useAuth();
  const [audits, setAudits] = useState(null);
  const [error, setError] = useState('');
  const [outletFilter, setOutletFilter] = useState('');

  useEffect(() => {
    auditService
      .getClientAudits()
      .then((data) => setAudits(data || []))
      .catch((err) => {
        setError(err.message);
        setAudits([]);
      });
  }, []);

  const outletOptions = useMemo(() => {
    const map = new Map();
    (audits || []).forEach((audit) => {
      if (audit.outlet_id && !map.has(String(audit.outlet_id))) {
        map.set(String(audit.outlet_id), outletText(audit) || 'Outlet');
      }
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [audits]);

  const grouped = useMemo(() => {
    const shown = (audits || []).filter(
      (audit) => !outletFilter || String(audit.outlet_id) === outletFilter,
    );
    return GROUPS.map((group) => {
      const items = shown.filter((audit) => statusOf(audit) === group.key);
      // Upcoming soonest first; live and finished newest first.
      if (group.key === 'upcoming') {
        items.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
      }
      return { ...group, items };
    }).filter((group) => group.items.length > 0);
  }, [audits, outletFilter]);

  const scope = audits ? scopeText(user, audits) : '';

  return (
    <div className="page client-audits">
      <header className="client-audits__header">
        <div>
          <p className="page__eyebrow">Client access</p>
          <h1>Your schedules</h1>
          <p className="client-audits__subtitle">
            Signed in as {user?.name || user?.email}
            {user?.client_title ? ` · ${user.client_title}` : ''}
            {scope ? ` · ${scope}` : ''}. Open a schedule to follow its live counts and
            comparisons.
          </p>
        </div>
        <button type="button" className="client-audits__logout" onClick={logout}>
          Sign out
        </button>
      </header>

      {error ? (
        <div className="client-audits__alert" role="alert">
          {error}
        </div>
      ) : null}

      {audits && outletOptions.length > 1 ? (
        <div className="client-audits__filters">
          <label>
            Outlet
            <select value={outletFilter} onChange={(event) => setOutletFilter(event.target.value)}>
              <option value="">All outlets ({outletOptions.length})</option>
              {outletOptions.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {audits && audits.length ? (
        <ScheduleKpiBoard
          schedules={audits.filter((audit) => !outletFilter || String(audit.outlet_id) === outletFilter)}
          storageKey="clientKpiSchedule"
        />
      ) : null}

      {audits === null ? (
        <PageLoader label="Loading your schedules…" />
      ) : audits.length === 0 ? (
        <p className="client-audits__empty">
          No schedules for your outlets yet. When the audit team schedules one, it appears here.
        </p>
      ) : (
        grouped.map((group) => (
          <section key={group.key} className="client-audits__group">
            <h2>
              {group.label} <span>{group.items.length}</span>
            </h2>
            <ul className="client-audits__list">
              {group.items.map((audit) => (
                <li key={audit.id}>
                  <Link to={`/audits/${audit.id}/portal`} className="client-audits__card">
                    <span className={`client-audits__status client-audits__status--${group.key}`}>
                      {group.key === 'live' ? 'Live' : group.key === 'upcoming' ? 'Upcoming' : 'Finished'}
                    </span>
                    <strong>{audit.name}</strong>
                    {audit.schedule_no ? (
                      <span className="client-audits__schedule">
                        <ScheduleNumber audit={audit} />
                      </span>
                    ) : null}
                    <span>
                      {[audit.organization_name, outletText(audit), audit.outlet_city]
                        .filter(Boolean)
                        .join(' · ') || 'Schedule'}
                    </span>
                    <small>
                      {format(parseISO(audit.start_at), 'MMM d, yyyy HH:mm')} –{' '}
                      {format(parseISO(audit.end_at), 'MMM d, yyyy HH:mm')}
                    </small>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

export default ClientAudits;
